// [FIX 18, 19] Оставлены только реально используемые импорты
import { Box, Button, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// [FIX 19] lodash используется только для set — импортируем точечно (пункт 32 не трогаем, но улучшаем в рамках 18/19)
import set from "lodash/set";

import { getDataSource } from "api/ds";
import { ReportTable } from "components/reportTable";
import { loginSelector } from "store/slices/loginSlice";
import { useSelector } from "react-redux";
import Loading from "components/Load";
import { getFile, post } from "api/apiBridges/httpBridge";
import { get } from "api/apiBridges/httpBridge";
import ClassicSearch from "components/classicSearch";
import { hoverStyle, hoverStyleTransformOnly, buttonColor, iconColor, buttonColorHover } from "styles/main";
import { useVirtualizer } from "@tanstack/react-virtual";
import { MemoTextField } from "components/MemoTextField";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import { CancelButton, NumInputDesign, OKButton, TextInputDesign } from "components/EIComponents/LittleComponents";
import { useLocalNotifications } from "components/Events";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ProductMoreInfo } from "utils/formatters";
import { DateMenu } from "components/RangeDate";
import CloseIcon from "@mui/icons-material/Close";
import { sortProducts } from "utils/globalSorts";
import { YearCalendar } from "@mui/x-date-pickers";
import { fetchAllProductsData } from "utils/productsData";

const FONT = {
    cardTitle: 18,
    cardTag: 16,
    cardBody: 16,
    cardPrice: 24,
};

// ============================================================
// ТЕГИ ТЕХНИЧЕСКИХ ЗАДАНИЙ — правила фильтрации ТЗ
// ============================================================
/**
 * Структура описания одного тега ТЗ.
 *
 * Поля:
 *  - name: строковое имя тега. Должно совпадать с тем,
 *          что реально приходит в `techTask.tags` с бэкенда.
 *
 *  - required: функция-условие (state) => boolean.
 *      Если функция возвращает true — тег обязателен для текущей сделки,
 *      т.е. ТЗ без этого тега не будет показано.
 *      Если возвращает false — тег не обязателен.
 *      Если required === undefined — тег не накладывает обязательств.
 *
 *  - forbidden: функция-условие (state) => boolean.
 *      Если функция возвращает true — тег запрещён для текущей сделки,
 *      т.е. ТЗ с этим тегом не будет показано.
 *      Если forbidden === undefined — тег не запрещает.
 *
 *  - info: текст подсказки (используется в UI, если понадобится).
 *
 * !!! ВАЖНО: конкретику условий (что именно проверять в state)
 *     вы дописываете сами в required/forbidden.
 *     Примеры ниже — это заготовки, которые вы можете менять.
 */
const TECH_TASK_TAGS = [
    {
        name: "WB",
        required: (state) => state.marketplace === "WB",
        forbidden: undefined,
        info: "Сделка непосредственно для маркетплейса WB",
    },

    {
        name: "OZON",
        required: (state) => state.marketplace === "OZON",
        forbidden: undefined,
        info: "Сделка непосредственно для маркетплейса OZON",
    },

    {
        name: "Другие направления",
        required: (state) => state.marketplace === "OTHER",
        forbidden: undefined,
        info: "Сделка целенаправлена ",
    },

    // Пример: тег "Без направления" запрещён, если выбрано конкретное направление
    {
        name: "Без направления",
        required: (state) => state.marketplace === "NONE",
        forbidden: (state) => state.marketplace && state.marketplace !== "NONE",
        info: "Техническое задание, применяемое без известного направления.",
    },

    // Пример: тег "Для отгрузки" обязателен, если цель сделки — отгрузка
    {
        name: "Для отгрузки",
        required: (state) => state.dealPurpose === "shipment",
        forbidden: undefined,
        info: "ТЗ, применяемое к сделкам на отгрузку.",
    },

    // Пример: тег "Для хранения" обязателен, если цель сделки — хранение
    {
        name: "Для хранения",
        required: (state) => state.dealPurpose === "storage",
        forbidden: undefined,
        info: "ТЗ, применяемое к сделкам на хранение.",
    },

    // Пример: тег "FBS" — заглушка, допишите условия сами
    {
        name: "FBS",
        required: undefined,
        forbidden: undefined,
        info: "ТЗ для обработки заказов FBS.",
    },
];

/**
 * Проверяет, подходит ли ТЗ под текущее состояние сделки.
 * Возвращает true, если ТЗ можно показывать.
 *
 * Логика:
 *  1. Собираем теги ТЗ (массив строк).
 *  2. Для каждого тега из TECH_TASK_TAGS:
 *     - если forbidden(state) === true И тег есть в ТЗ — блокируем.
 *     - если required(state) === true И тега нет в ТЗ — блокируем.
 *  3. Если ни одного блокирующего условия не сработало — ТЗ подходит.
 */
function isTechTaskAllowed(techTask, state) {
    const tags = Array.isArray(techTask?.tags) ? techTask.tags : [];

    for (const tagDef of TECH_TASK_TAGS) {
        const hasTag = tags.includes(tagDef.name);

        // Запрет: тег в ТЗ, но forbidden(state) === true
        if (hasTag && tagDef.forbidden && tagDef.forbidden(state)) {
            return false;
        }

        // Обязательность: required(state) === true, но тега нет в ТЗ
        if (!hasTag && tagDef.required && tagDef.required(state)) {
            return false;
        }
    }

    return true;
}

// ============================================================
// КОНФИГ БЛОКОВ — декларативное описание условий появления
// ============================================================
/**
 * Каждый блок описывается:
 *  - id: уникальный ключ
 *  - title: заголовок блока
 *  - always: true — блок доступен всегда, независимо от предыдущих выборов
 *  - condition: (state) => boolean — функция, определяющая доступность блока
 *  - render: (ctx) => JSX — рендер содержимого
 *
 * Благодаря такому конфигу блоки появляются постепенно,
 * а условия легко дорабатывать в одном месте.
 */
const DEAL_BLOCKS = [
    // ---------- Блок 1: Тип операции ----------
    {
        id: "operationType",
        title: "Тип операции",
        always: true,
        render: ({ state, setState }) => {
            const OPTIONS = [
                {
                    value: "supply",
                    label: "Поставка товара",
                    info: "Сделка на поставку товара на склад. Потребуется указать данные для забора груза.",
                },
                {
                    value: "warehouse",
                    label: "Работа с товаром на складе",
                    info: "Сделка по обработке товара, который уже находится на складе. Забор груза не требуется.",
                },
            ];
            return (
                <OptionsGrid
                    // @ts-ignore
                    options={OPTIONS}
                    selected={state.operationType}
                    onSelect={(v) => setState((p) => ({ ...p, operationType: v }))}
                />
            );
        },
    },

    // ---------- Блок 2: Цель сделки ----------
    {
        id: "dealPurpose",
        title: "Цель сделки",
        always: true,
        render: ({ state, setState }) => {
            const OPTIONS = [
                {
                    value: "storage",
                    label: "Для хранения на складе",
                    info: "Товар остаётся на складе. Параметры отгрузки указывать не нужно.",
                },
                {
                    value: "shipment",
                    label: "Для дальнейшей отгрузки товара",
                    info: "Товар обрабатывается для последующей отгрузки. Потребуется указать параметры отгрузки.",
                },
            ];
            return (
                <OptionsGrid
                    // @ts-ignore
                    options={OPTIONS}
                    selected={state.dealPurpose}
                    onSelect={(v) => setState((p) => ({ ...p, dealPurpose: v }))}
                />
            );
        },
    },

    // ---------- Блок 3: Маркетплейс / направление ----------
    {
        id: "marketplace",
        title: "Направление",
        always: true,
        render: ({ state, setState }) => {
            const OPTIONS = [
                { value: "WB", label: "WB", info: "Отгрузка на маркетплейс Wildberries." },
                { value: "OZON", label: "OZON", info: "Отгрузка на маркетплейс OZON." },
                {
                    value: "OTHER",
                    label: "Другие направления",
                    info: "Отгрузка на направление, не поддерживаемое как отдельный маркетплейс.",
                },
                { value: "NONE", label: "Без направления", info: "Направление отгрузки заранее неизвестно." },
            ];

            return (
                <OptionsGrid
                    // @ts-ignore
                    options={OPTIONS}
                    selected={state.marketplace}
                    onSelect={(v) => setState((p) => ({ ...p, marketplace: v }))}
                />
            );
        },
    },

    // ---------- Блок 4: Данные для забора (только при "Поставка товара") ----------
    {
        id: "pickup",
        title: "Данные для забора товара",
        condition: (state) => state.operationType === "supply",
        render: ({ state, setState }) => {
            const upd = (patch) => setState((p) => ({ ...p, ...patch }));
            return (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px 24px",
                        width: "100%",
                        padding: "5px",
                        boxSizing: "border-box",
                    }}
                >
                    {/* Левая колонка — контактные данные */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>
                            Контактные данные
                        </Typography>
                        <TextInputDesign
                            title={"Адрес для забора:"}
                            val={state.pickup_addr}
                            onChange={(v) => upd({ pickup_addr: v })}
                        />
                        <TextInputDesign
                            title={"Контакт:"}
                            val={state.pickup_contact}
                            onChange={(v) => upd({ pickup_contact: v })}
                        />
                        <TextInputDesign
                            title={"Подписание груза:"}
                            val={state.pickup_sign}
                            onChange={(v) => upd({ pickup_sign: v })}
                        />
                    </div>

                    {/* Правая колонка — параметры груза */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>
                            Параметры груза
                        </Typography>
                        <div style={{ display: "flex", flexDirection: "row", gap: "12px" }}>
                            <NumInputDesign
                                title={"Кол-во мест"}
                                val={state.countBale}
                                onChange={(v) => upd({ countBale: v })}
                            />
                            <NumInputDesign
                                title={"Объём в м³"}
                                val={state.pickup_volume}
                                onChange={(v) => upd({ pickup_volume: v })}
                            />
                        </div>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginTop: "2px" }}>
                            <span style={{ marginRight: "8px", color: "#2c3e50", fontSize: "13px" }}>
                                Дата забора:
                            </span>
                            <DateMenu
                                date={state.pickup_date}
                                onChange={(v) => upd({ pickup_date: v })}
                            />
                        </div>
                    </div>
                </div>
            );
        },
    },

    // ---------- Блок 4 (альтернатива): Параметры отгрузки ----------
    {
        id: "shipment",
        title: "Параметры отгрузки",
        condition: (state) => state.dealPurpose === "shipment",
        render: ({ state, setState }) => {
            const upd = (patch) => setState((p) => ({ ...p, ...patch }));
            return (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px 24px",
                        width: "100%",
                        padding: "5px",
                        boxSizing: "border-box",
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>
                            Склады
                        </Typography>
                        <TextInputDesign
                            title={"Транзитный склад:"}
                            val={state.warehouse_tranzit}
                            onChange={(v) => upd({ warehouse_tranzit: v })}
                        />
                        <TextInputDesign
                            title={"Склад назначения:"}
                            val={state.warehouse_market}
                            onChange={(v) => upd({ warehouse_market: v })}
                        />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "gray", fontWeight: 600 }}>
                            Даты
                        </Typography>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginTop: "2px" }}>
                            <span style={{ marginRight: "8px", color: "#2c3e50", fontSize: "13px" }}>
                                Дата отгрузки:
                            </span>
                            <DateMenu
                                date={state.date_shipment}
                                onChange={(v) => upd({ date_shipment: v })}
                            />
                        </div>
                    </div>
                </div>
            );
        },
    },

    // ---------- Блок 5: Товары (всегда) ----------
    {
        id: "products",
        always: true,
        render: ({ state, setState, ctx }) => (
            <DealProductsBlock
                // @ts-ignore
                state={state}
                setState={setState}
                avaibleProducts={ctx.avaibleProducts}
                avaibleTechTasks={ctx.avaibleTechTasks}
            />
        ),
    },
];

// ============================================================
// Вспомогательный компонент: сетка вариантов выбора (radio-group)
// ============================================================
/**
 * Одиночный выбор. Клик по варианту выбирает его,
 * повторный клик по выбранному — снимает выбор.
 * Подсказка показывается через Tooltip (el.info).
 */
const OptionsGrid = memo(function OptionsGrid({
    // @ts-ignore
    options,
    // @ts-ignore
    selected,
    // @ts-ignore
    onSelect,
}) {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                rowGap: "10px",
                columnGap: "1.2%",
                flexWrap: "wrap",
                padding: "5px",
                width: "100%",
            }}
        >
            {options.map((el) => {
                const isSel = selected === el.value;
                return (
                    <Tooltip key={el.value} title={el.info || ""} arrow>
                        <div
                            onClick={() => onSelect(isSel ? null : el.value)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px 15px",
                                backgroundColor: "white",
                                borderRadius: "8px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                border: isSel ? "1px solid #208f84" : "1px solid #e0e0e0",
                                transition: "all 0.2s ease",
                                width: "19%",
                                position: "relative",
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            <div
                                style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: isSel ? "#208f84" : "#ffffff",
                                    border: isSel ? "1px solid #208f84" : "1px solid #cfcfcf",
                                    flexShrink: 0,
                                }}
                            />
                            <div style={{ width: "calc(100% - 12px)" }}>
                                <Typography
                                    style={{
                                        fontWeight: "600",
                                        fontSize: "12px",
                                        color: "#2c3e50",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {el.label}
                                </Typography>
                            </div>
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );
});

// ============================================================
// Блок товаров: список + добавление ТЗ к каждому товару
// ============================================================
const DealProductsBlock = memo(function DealProductsBlock({
    // @ts-ignore
    state,
    // @ts-ignore
    setState,
    // @ts-ignore
    avaibleProducts,
    // @ts-ignore
    avaibleTechTasks,
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [ttDrawer, setTtDrawer] = useState({ open: false, productId: null });

    const products = state.products || [];

    // Отсортированный список доступных товаров (для шторки)
    const sortedAvaibleProducts = useMemo(
        () => sortProducts(avaibleProducts || []),
        [avaibleProducts]
    );

    /**
     * Для каждого товара считаем список ТЗ, которые ему доступны
     * с учётом:
     *  - barcode товара должен быть в techTask.barcodes
     *  - ТЗ должно проходить isTechTaskAllowed(techTask, state)
     *
     * Возвращает Map: barcode -> массив подходящих ТЗ.
     */
    const allowedTTByBarcode = useMemo(() => {
        const map = new Map();
        const list = avaibleTechTasks || [];

        for (const tt of list) {
            if (!isTechTaskAllowed(tt, state)) continue;

            const barcodes = Array.isArray(tt?.barcodes) ? tt.barcodes.map(String) : [];
            if (barcodes.length === 0) continue;

            for (const bc of barcodes) {
                if (!map.has(bc)) map.set(bc, []);
                map.get(bc).push(tt);
            }
        }
        return map;
    }, [avaibleTechTasks, state]);

    // Товары, у которых доступно ровно одно ТЗ — выбираем автоматически
    useEffect(() => {
        setState((prev) => {
            const list = prev.products || [];
            if (list.length === 0) return prev;

            let changed = false;
            const next = list.map((p) => {
                const allowed = allowedTTByBarcode.get(String(p.barcode)) || [];
                const current = p.techTaskIds || [];

                // Если уже что-то выбрано — не трогаем
                if (current.length > 0) return p;

                // Если доступно ровно одно — выбираем
                if (allowed.length === 1) {
                    changed = true;
                    return { ...p, techTaskIds: [allowed[0].id] };
                }

                return p;
            });

            return changed ? { ...prev, products: next } : prev;
        });
    }, [allowedTTByBarcode, setState]);

    // Добавить товар
    const addProduct = useCallback(
        (product) => {
            const bc = product?.size?.barcode ?? product?.data?.barcode;
            setState((prev) => {
                const list = prev.products || [];
                if (list.some((p) => String(p.barcode) === String(bc))) return prev;
                return {
                    ...prev,
                    products: [...list, { barcode: bc, product, techTaskIds: [] }],
                };
            });
        },
        [setState]
    );

    // Добавить сразу все товары из переданного списка
    // (используется кнопкой «Добавить все» — она передаёт уже
    //  отфильтрованный внутри шторки список)
    const addManyProducts = useCallback(
        (productList) => {
            setState((prev) => {
                const cur = prev.products || [];
                const existing = new Set(cur.map((p) => String(p.barcode)));
                const toAdd = [];
                for (const product of productList || []) {
                    const bc = product?.size?.barcode ?? product?.data?.barcode;
                    if (bc === undefined || bc === null) continue;
                    if (existing.has(String(bc))) continue;
                    existing.add(String(bc));
                    toAdd.push({ barcode: bc, product, techTaskIds: [] });
                }
                if (toAdd.length === 0) return prev;
                return { ...prev, products: [...cur, ...toAdd] };
            });
        },
        [setState]
    );

    // Удалить товар
    const removeProduct = useCallback(
        (barcode) => {
            setState((prev) => ({
                ...prev,
                products: (prev.products || []).filter((p) => String(p.barcode) !== String(barcode)),
            }));
        },
        [setState]
    );

    // Привязать ТЗ к товару
    const addTechTask = useCallback(
        (barcode, ttId) => {
            setState((prev) => ({
                ...prev,
                products: (prev.products || []).map((p) => {
                    if (String(p.barcode) !== String(barcode)) return p;
                    const ids = p.techTaskIds || [];
                    if (ids.includes(ttId)) return p;
                    return { ...p, techTaskIds: [...ids, ttId] };
                }),
            }));
        },
        [setState]
    );

    // Отвязать ТЗ от товара
    const removeTechTask = useCallback(
        (barcode, ttId) => {
            setState((prev) => ({
                ...prev,
                products: (prev.products || []).map((p) => {
                    if (String(p.barcode) !== String(barcode)) return p;
                    return { ...p, techTaskIds: (p.techTaskIds || []).filter((id) => id !== ttId) };
                }),
            }));
        },
        [setState]
    );

    return (
        <div style={{ width: "100%" }}>
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                }}
            >
                <Typography sx={{ color: "gray" }}>Товары в сделке</Typography>
                <Button
                    variant="contained"
                    onClick={() => setDrawerOpen(true)}
                    sx={{
                        backgroundColor: buttonColor,
                        borderRadius: "10px",
                        textTransform: "none",
                        "&:hover": { backgroundColor: buttonColorHover },
                    }}
                >
                    Добавить товар
                </Button>
            </div>

            {products.length === 0 ? (
                <div style={{ padding: "10px", color: "#999", fontSize: "14px" }}>
                    Товары не добавлены
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {sortProducts(products).map((p) => (
                        <DealProductRow
                            key={String(p.barcode)}
                            // @ts-ignore
                            item={p}
                            techTasks={avaibleTechTasks}
                            allowedTechTasks={allowedTTByBarcode.get(String(p.barcode)) || []}
                            onRemove={() => removeProduct(p.barcode)}
                            onOpenTechTasks={() => setTtDrawer({ open: true, productId: p.barcode })}
                            onRemoveTechTask={(ttId) => removeTechTask(p.barcode, ttId)}
                        />
                    ))}
                </div>
            )}

            {/* Шторка выбора товаров */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: "50%", minWidth: 480 } }}
                keepMounted={false}
            >
                {drawerOpen && (
                    <ProductPickerDrawer
                        // @ts-ignore
                        avaibleProducts={sortedAvaibleProducts}
                        excludeBarcodes={products.map((p) => String(p.barcode))}
                        onAdd={(product) => addProduct(product)}
                        onAddMany={(list) => addManyProducts(list)}
                    />
                )}
            </Drawer>

            {/* Шторка выбора ТЗ для товара */}
            <Drawer
                anchor="right"
                open={ttDrawer.open}
                onClose={() => setTtDrawer({ open: false, productId: null })}
                PaperProps={{ sx: { width: "40%", minWidth: 400 } }}
                keepMounted={false}
            >
                {ttDrawer.open && (
                    <TechTaskPickerDrawer
                        // @ts-ignore
                        techTasks={allowedTTByBarcode.get(String(ttDrawer.productId)) || []}
                        selectedIds={
                            (products.find((p) => String(p.barcode) === String(ttDrawer.productId))?.techTaskIds) || []
                        }
                        onAdd={(ttId) => addTechTask(ttDrawer.productId, ttId)}
                        onRemove={(ttId) => removeTechTask(ttDrawer.productId, ttId)}
                    />
                )}
            </Drawer>
        </div>
    );
});

// ============================================================
// Строка товара: ProductMoreInfo + количество + плашки ТЗ
// ============================================================
const DealProductRow = memo(function DealProductRow({
    // @ts-ignore
    item,
    // @ts-ignore
    techTasks,
    // @ts-ignore
    allowedTechTasks,
    // @ts-ignore
    onRemove,
    // @ts-ignore
    onOpenTechTasks,
    // @ts-ignore
    onRemoveTechTask,
}) {
    const ttMap = useMemo(() => {
        const m = new Map();
        (techTasks || []).forEach((tt) => m.set(tt.id, tt));
        return m;
    }, [techTasks]);

    const selectedTT = (item.techTaskIds || [])
        .map((id) => ttMap.get(id))
        .filter(Boolean);

    const updateCount = useCallback(
        (v) => {
            // при необходимости можно прокинуть onChangeCount из родителя
        },
        []
    );

    const noAllowed = (allowedTechTasks || []).length === 0;

    return (
        <div
            style={{
                display: "flex",
                alignItems: "stretch",
                gap: "10px",
                padding: "10px",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
        >
            {/* Инфо о товаре */}
            <div style={{ width: "40%", minWidth: 0 }}>
                <ProductMoreInfo value={item.product?.data} />
            </div>

            {/* Количество */}
            <div style={{ width: "120px", display: "flex", alignItems: "center" }}>
                <NumInputDesign
                    title={"Кол-во"}
                    val={item.count ?? 1}
                    onChange={updateCount}
                />
            </div>

            {/* Область ТЗ */}
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    alignItems: "center",
                    padding: "5px",
                    border: "1px dashed #cfcfcf",
                    borderRadius: "8px",
                    minHeight: "44px",
                }}
            >
                {selectedTT.map((tt) => (
                    <div
                        key={tt.id}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            backgroundColor: iconColor,
                            borderRadius: "12px",
                            padding: "5px 8px 5px 10px",
                        }}
                    >
                        <Typography sx={{ fontSize: "12px", color: buttonColor, lineHeight: "14px" }}>
                            {tt.name || tt.version || "ТЗ"}
                        </Typography>
                        <IconButton
                            size="small"
                            disableRipple
                            onClick={() => onRemoveTechTask(tt.id)}
                            sx={{ padding: "2px", color: buttonColor }}
                        >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                    </div>
                ))}

                {noAllowed && selectedTT.length === 0 && (
                    <Typography sx={{ fontSize: "12px", color: "#c0392b" }}>
                        Нет доступных ТЗ для этого товара
                    </Typography>
                )}

                {!noAllowed && (
                    <IconButton
                        size="small"
                        disableRipple
                        onClick={onOpenTechTasks}
                        sx={{
                            backgroundColor: "rgba(7, 132, 118, 0.08)",
                            color: buttonColor,
                            "&:hover": hoverStyleTransformOnly,
                        }}
                    >
                        <AddIcon sx={{ fontSize: "18px" }} />
                    </IconButton>
                )}
            </div>

            {/* Удалить товар */}
            <IconButton
                size="small"
                disableRipple
                onClick={onRemove}
                sx={{ color: "#c0392b", "&:hover": hoverStyleTransformOnly }}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </div>
    );
});

// ============================================================
// Шторка выбора товаров
// ============================================================
const ProductPickerDrawer = memo(function ProductPickerDrawer({
    // @ts-ignore
    avaibleProducts,
    // @ts-ignore
    excludeBarcodes,
    // @ts-ignore
    onAdd,
    // @ts-ignore
    onAddMany,
}) {
    const listRef = useRef(null);
    const ROW_HEIGHT = 120;

    const [checked, setChecked] = useState(false);
    const [filter, setFilter] = useState("");

    const excludeSet = useMemo(() => new Set((excludeBarcodes || []).map(String)), [excludeBarcodes]);

    const filtered = useMemo(() => {
        let list = (avaibleProducts || []).filter((p) => {
            const bc = p?.size?.barcode ?? p?.data?.barcode;
            return bc !== undefined && bc !== null && !excludeSet.has(String(bc));
        });

        if (checked && filter) {
            const q = filter.toLowerCase();
            list = list.filter((p) =>
                [p?.data?.title, p?.data?.vendorCode, p?.size?.vendorCode, p?.size?.barcode, p?.data?.brand]
                    .some((f) => f && String(f).toLowerCase().includes(q))
            );
        }
        return list;
    }, [avaibleProducts, excludeSet, checked, filter]);

    const virtualizer = useVirtualizer({
        count: filtered.length,
        getScrollElement: () => listRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 10,
        getItemKey: (index) => {
            const p = filtered[index];
            return p?.id ?? String(p?.size?.barcode ?? p?.data?.barcode ?? index);
        },
    });

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        virtualizer.measure();
        const ro = new ResizeObserver(() => virtualizer.measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [virtualizer]);

    // «Добавить все» добавляет именно отфильтрованный список —
    // т.е. если пользователь что-то искал, добавятся только найденные
    const handleAddAll = useCallback(() => {
        onAddMany(filtered);
    }, [onAddMany, filtered]);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "20px", gap: "15px", boxSizing: "border-box" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: buttonColor }}>
                Добавить товары
            </Typography>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                    <ClassicSearch
                        setChecked={setChecked}
                        setFilter={setFilter}
                        checked={checked}
                        // @ts-ignore
                        customWidth={"100%"}
                    />
                </div>
                <Button
                    variant="contained"
                    onClick={handleAddAll}
                    disabled={filtered.length === 0}
                    sx={{
                        backgroundColor: buttonColor,
                        borderRadius: "10px",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        "&:hover": { backgroundColor: buttonColorHover },
                        "&.Mui-disabled": { backgroundColor: "#cfcfcf", color: "#fff" },
                    }}
                >
                    Добавить все
                </Button>
            </div>

            <div ref={listRef} className="custom-scrollbar" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {filtered.length === 0 ? (
                    <div style={{ padding: "10px", color: "#999" }}>Нет доступных товаров</div>
                ) : (
                    <div style={{ height: virtualizer.getTotalSize(), width: "100%", position: "relative" }}>
                        {virtualizer.getVirtualItems().map((vi) => {
                            const p = filtered[vi.index];
                            return (
                                <div
                                    key={vi.key}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: ROW_HEIGHT,
                                        transform: `translateY(${vi.start}px)`,
                                        paddingBottom: "10px",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <ProductPickerRow
                                        // @ts-ignore
                                        product={p}
                                        onAdd={onAdd}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
});

const ProductPickerRow = memo(function ProductPickerRow({
    // @ts-ignore
    product,
    // @ts-ignore
    onAdd,
}) {
    const handleAdd = useCallback(() => onAdd(product), [onAdd, product]);
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px",
                background: "white",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <ProductMoreInfo value={product.data} />
            </div>
            <IconButton
                onClick={handleAdd}
                sx={{ color: buttonColor, "&:hover": { backgroundColor: "rgba(7, 132, 118, 0.08)" } }}
            >
                <ArrowForwardIosIcon />
            </IconButton>
        </div>
    );
});

// ============================================================
// Шторка выбора ТЗ для товара
// ============================================================
const TechTaskPickerDrawer = memo(function TechTaskPickerDrawer({
    // @ts-ignore
    techTasks,
    // @ts-ignore
    selectedIds,
    // @ts-ignore
    onAdd,
    // @ts-ignore
    onRemove,
}) {
    const [checked, setChecked] = useState(false);
    const [filter, setFilter] = useState("");

    const filtered = useMemo(() => {
        let list = techTasks || [];
        if (checked && filter) {
            const q = filter.toLowerCase();
            list = list.filter((tt) => String(tt.name || "").toLowerCase().includes(q));
        }
        return list;
    }, [techTasks, checked, filter]);

    const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "20px", gap: "15px", boxSizing: "border-box" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold", color: buttonColor }}>
                Технические задания
            </Typography>

            <ClassicSearch
                setChecked={setChecked}
                setFilter={setFilter}
                checked={checked}
                // @ts-ignore
                customWidth={"100%"}
            />

            <div className="custom-scrollbar" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {filtered.length === 0 ? (
                    <div style={{ padding: "10px", color: "#999" }}>Нет технических заданий</div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {filtered.map((tt) => {
                            const isSel = selectedSet.has(tt.id);
                            const tags = Array.isArray(tt?.tags) ? tt.tags : [];
                            return (
                                <div
                                    key={tt.id}
                                    onClick={() => (isSel ? onRemove(tt.id) : onAdd(tt.id))}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: "10px",
                                        padding: "10px 15px",
                                        backgroundColor: "white",
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                        border: isSel ? "1px solid #208f84" : "1px solid #e0e0e0",
                                        cursor: "pointer",
                                        userSelect: "none",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "6px",
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "13px", color: "#2c3e50", fontWeight: 600 }}>
                                            {tt.name || tt.version || "ТЗ"}
                                        </Typography>
                                        {tags.length > 0 && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: "6px",
                                                }}
                                            >
                                                {tags.map((tag) => (
                                                    <div
                                                        key={tag}
                                                        style={{
                                                            backgroundColor: iconColor,
                                                            borderRadius: "10px",
                                                            padding: "2px 8px",
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: "11px",
                                                                color: buttonColor,
                                                                lineHeight: "14px",
                                                            }}
                                                        >
                                                            {tag}
                                                        </Typography>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                            backgroundColor: isSel ? "#208f84" : "#fff",
                                            border: isSel ? "1px solid #208f84" : "1px solid #cfcfcf",
                                            flexShrink: 0,
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
});

// ============================================================
// Основной компонент
// ============================================================
export function DealSettings({}) {
    const { user_id, company_id } = useSelector(loginSelector);
    const { showNotification } = useLocalNotifications();

    const [loading, setLoading] = useState(true);

    // Единый стейт сделки — так проще прокидывать в конфиг блоков
    const [state, setState] = useState({
        operationType: null,       // "supply" | "warehouse"
        dealPurpose: null,         // "storage" | "shipment"
        marketplace: null,         // "WB" | "OZON" | "OTHER" | "NONE" | null

        // забор
        pickup_addr: "",
        pickup_contact: "",
        pickup_sign: "",
        countBale: 0,
        pickup_volume: 0,
        pickup_date: null,

        // отгрузка
        warehouse_tranzit: "",
        warehouse_market: "",
        date_shipment: null,

        // товары
        products: [], // [{ barcode, product, techTaskIds: [] }]
    });

    /** @type {[any, Function]} */
    const [avaibleProducts, setAvaibleProducts] = useState([]);
    /** @type {[any, Function]} */
    const [avaibleTechTasks, setAvaibleTechTasks] = useState([]);
    /** @type {[any, Function]} */
    const [marketLinks, setMarketLinks] = useState({});      // custom_barcode -> { wb, ozon, id }
    /** @type {[any, Function]} */
    const [marketData, setMarketData] = useState({ wb: [], ozon: [] });

    // Загрузка товаров и тз
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);

                // 1) Товары и связи между маркетами — из утилиты
                // 2) ТЗ — отдельным запросом, как и раньше
                const [productsResult, respTT] = await Promise.all([
                    fetchAllProductsData({ user_id, company_id, showNotification }),
                    get("tech_task/getClientTechTasks", { uid: user_id }),
                ]);

                if (cancelled) return;

                setAvaibleProducts(productsResult.dataCUSTOM || []);
                setMarketLinks(productsResult.customMap || {});
                setMarketData({
                    wb: productsResult.dataWB || [],
                    ozon: productsResult.dataOZON || [],
                });
                setAvaibleTechTasks(respTT || []);
            } catch (e) {
                console.log(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user_id, company_id]);

    // Рендер блоков по конфигу
    const blocks = useMemo(() => {
        const ctx = { avaibleProducts, avaibleTechTasks };
        return DEAL_BLOCKS.filter((b) => b.always || b.condition?.(state)).map((b) => ({
            id: b.id,
            title: b.title,
            content: b.render({ state, setState, ctx }),
        }));
    }, [state, avaibleProducts, avaibleTechTasks]);

    const save = useCallback(() => {
        // Базовая валидация
        if (!state.operationType) {
            showNotification({ id: "dealErr", type: "error", message: "Выберите тип операции" });
            return;
        }
        if (!state.dealPurpose) {
            showNotification({ id: "dealErr", type: "error", message: "Выберите цель сделки" });
            return;
        }
        if (!state.marketplace) {
            showNotification({ id: "dealErr", type: "error", message: "Выберите направление" });
            return;
        }
        if (!state.products || state.products.length === 0) {
            showNotification({ id: "dealErr", type: "error", message: "Добавьте хотя бы один товар" });
            return;
        }
    }, [state, showNotification]);

    const create = async () => {
        setLoading(true)
        let tmpData = JSON.parse(JSON.stringify(state.products))
        try {
            if (tmpData.length === 0) {
                showNotification({
                    id: 'Null Products', 
                    type: 'error', 
                    message: 'Ни один товар не добавлен' 
                });
                return
            }

            let market = "custom"
            if (state.marketplace === "WB") {
                market = 'wb'
            }
            if (state.marketplace === "OZON") {
                market = 'ozon'
            }
            
            let prslist = []

            if (market !== 'custom') {
                for (let cpr of tmpData) {
                    if (marketLinks[cpr.size.barcode][market]) {
                        prslist.push({...marketLinks[cpr.size.barcode][market], techTaskIds: cpr.techTaskIds})
                    }
                }
            }
            else {
                prslist = tmpData
            }
            


            const $data = [];
            let err = false
            let noComplectCount = false
            let units = 0
            for (const pr of prslist) {
                // Собираем объект товара
                const item = { nmID: pr.nmID, countProduct: pr.countProduct, ...pr.data, size: pr.size };
                delete item.size.countQr

                if (pr.techTaskIds.length === 0) {
                    err = true
                    return
                }
                let techTasks = pr.techTaskIds.map((ttid) => {return avaibleTechTasks.find((fel) => fel.id === ttid)}) 
                
                item.techTasks = techTasks
                units += parseInt(item.countProduct);
                $data.push(item);
            }

            if (err) {
                showNotification({
                    id: 'NOTZ', 
                    type: 'error', 
                    message: 'У некоторых товаров не указано техническое задание' 
                });
                return 
            }
            if (noComplectCount) {
                showNotification({
                    id: 'NOTZComplectCount', 
                    type: 'error', 
                    message: 'Расходников недостаточно для сборки заявленного количества товаров' 
                });
                return 
            }

            const cli = await get('Clients/get', {cid:user_id});
            let sku = $data.length;
            let nd = await post('Deal/create', 
                {
                    products: $data, 
                    count_bale: 1, 
                    warehouse_tranzit: state.warehouse_tranzit, 
                    warehouse_market: state.warehouse_market, 
                    date_shipment: state.date_shipment, 
                    id_company: company_id, 
                    id_client: user_id, 
                    id_manager: Number(cli.message.manager_id), 
                    market: market, 
                    number_order: "", 
                    status: 1, 
                    type: "pik_goods",
                    tags: [], //TODO add TAGS
                    data:{
                            sku,
                            units,
                            end_date:state.date_shipment,
                            pickup: {
                                contact:state.pickup_contact,
                                addr:state.pickup_addr,
                                sign:state.pickup_sign,
                                volume:state.pickup_volume,
                                date: state.pickup_date
                            },
                            // ...(MW && { MW }) // Добавляем MW только если оно не undefined
                        },
                })
            if (nd?.id) {
                //Лог о содании сделки
                await post("Logs/create", {
                    deal_id:nd.id,
                    user_type:"cli",
                    user_id,
                    type: "create_deal",
                    data: {
                        sku,
                        units,
                    }
                })
                // socket.emit('add_new_deal', {company_id, type: needComplectCount !== $data.length? "pik_goods": "moving_deals", id:nd.id});
                // setShouldNavigate(true);
            }
            else {
                showNotification({
                    id: 'createDealErr', 
                    type: 'error', 
                    message: 'Ошибка при создании сделки, сообщите администратору' 
                });
            }
        }   
        catch (e) {
            console.log(e)
        }
        finally {
            setLoading(false)
        }
    }

    if (loading) return <Loading />;

    return (
        <div
            className="custom-scrollbar"
            style={{
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "30px",
                    padding: "2%",
                    width: "100%",
                    boxSizing: "border-box",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        justifyContent: "space-between",
                        padding: "0 2% 0 2%",
                    }}
                >
                    <CancelButton action={() => {}} text={"ВЕРНУТЬСЯ"} />
                    <OKButton action={save} text={"СОЗДАТЬ"} />
                </div>

                {/* Блоки по конфигу */}
                {blocks.map((b) => (
                    <BaseCard
                        key={b.id}
                        // @ts-ignore
                        rightWidth={"100%"}
                        leftContent={undefined}
                        rightContent={
                            <div
                                style={{
                                    padding: "5px",
                                    paddingLeft: "10px",
                                    borderRadius: "10px",
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}
                            >
                                {b.title && <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "10px",
                                    }}
                                >
                                    <Typography sx={{ color: "gray" }}>{b.title}</Typography>
                                </div>}
                                {b.content}
                            </div>
                        }
                    />
                ))}
            </div>
        </div>
    );
}

// ============================================================
// BaseCard — копия из TeckTaskSettings (можно вынести в общий модуль)
// ============================================================
const BaseCard = memo(function BaseCard({
    // @ts-ignore
    leftContent,
    // @ts-ignore
    rightContent,
    // @ts-ignore
    leftWidth = "40%",
    // @ts-ignore
    rightWidth = "60%",
    // @ts-ignore
    leftBackground = "#F5F9FA",
    // @ts-ignore
    rightBackground = "rgba(255, 255, 255, 0.5)",
}) {
    return (
        <div
            style={{
                width: "96%",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                margin: "0% 2% 0% 2%",
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.0)",
                overflow: "hidden",
            }}
        >
            {leftContent && (
                <div
                    style={{
                        width: leftWidth,
                        backgroundColor: leftBackground,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        padding: "1%",
                    }}
                >
                    {leftContent}
                </div>
            )}
            {rightContent && (
                <div
                    style={{
                        width: rightWidth,
                        backgroundColor: rightBackground,
                        padding: "1%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    {rightContent}
                </div>
            )}
        </div>
    );
});